/**
Author - Sahil Dhingra
Git - https://github.com/sahildhingra-221
**/

import React from "react";
import Head from "next/head";

import {WealthyEnv} from "helpers";

import {
  NewFooter,
  GetStartedSection,
  PriceTable,
  OtherCharges,
  BrokingBannerCta,
  PricingBanner,
  RegulatoryCharges,
  DematCharges,
  SegmentAnalytics,
  BrokerageCharges
} from "components";

import GlobalStyle from "static/next/css/PartnerGlobalStyle";

const Pricing = ({seoData}) => {
  return (
    <>
      <Head>
        <title>{seoData.title}</title>
        <meta content={seoData.title} name="title" />
        <meta content={seoData.description} name="description" />
        <meta content={seoData.keywords} name="keywords" />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Wealthy" />
        <meta property="og:url" content={seoData.canonicalUrl} />
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:image" content={seoData.ogImage} />

        <meta property="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@wealthy_in" />
        <meta property="twitter:url" content={seoData.canonicalUrl} />
        <meta property="twitter:title" content={seoData.title} />
        <meta property="twitter:description" content={seoData.description} />
        <meta property="twitter:image" content={seoData.ogImage} />

        <link href={seoData.canonicalUrl} rel="canonical" />
        <link href={seoData.favicon} rel="icon" />
        {seoData.schema && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(seoData.schema)
            }}
          />
        )}
        <link href="https://fonts.googleapis.com/css2?family=Marcellus&display=swap" rel="stylesheet" />
      </Head>
      <SegmentAnalytics />
      <GlobalStyle />
      <PricingBanner />
      <PriceTable />
      <BrokerageCharges />
      <GetStartedSection />
      <RegulatoryCharges />
      <DematCharges />
      <OtherCharges />
      <BrokingBannerCta />
      <NewFooter />
    </>
  );
};

export async function getStaticProps() {
  const seoData = {
    title: "Wealthy Pricing - Broking, Mutual Funds & Insurance Plans",
    description: "Get Wealthy's complete pricing details and brokerage charges. Zero account opening charges. Transparent pricing for stocks, mutual funds, insurance and more.",
    keywords: "wealthy pricing, brokerage charges, mutual fund charges, insurance plans pricing, demat account charges, stock trading fees, zero brokerage, wealthy broking charges",
    ogImage: "https://i.wlycdn.com/wealthy-home-page/client-metaimage.png",
    canonicalUrl: `${WealthyEnv.URL}/pricing`,
    favicon: "https://i.wlycdn.com/wealthy-home-page/new-fav-icon.png",
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebSite",
          "@id": `${WealthyEnv.URL}/pricing#website`,
          name: "Wealthy",
          url: `${WealthyEnv.URL}/pricing`,
          potentialAction: {
            "@type": "SearchAction",
            target: `${WealthyEnv.URL}/search?q={search_term_string}`,
            "query-input": "required name=search_term_string"
          }
        },
        {
          "@type": "Organization",
          "@id": `${WealthyEnv.URL}#organization`,
          name: "Wealthy",
          url: `${WealthyEnv.URL}`,
          logo: {
            "@type": "ImageObject",
            url: `${WealthyEnv.HOMEPAGE_CDN_URL}/wealthy-new-purple-logo.svg`
          },
          sameAs: [
            "https://www.linkedin.com/company/wealthy-in/",
            "https://twitter.com/wealthy_in"
          ]
        },
        {
          "@type": "WebPage",
          "@id": `${WealthyEnv.URL}/pricing#webpage`,
          url: `${WealthyEnv.URL}/pricing`,
          name: "Wealthy Pricing - Broking, Mutual Funds & Insurance Plans",
          description: "Get Wealthy's complete pricing details and brokerage charges. Zero account opening charges. Transparent pricing for stocks, mutual funds, insurance and more.",
          isPartOf: {
            "@id": `${WealthyEnv.URL}/pricing#website`
          },
          about: {
            "@id": `${WealthyEnv.URL}#organization`
          }
        }
      ]
    }
  };

  return {
    props: {
      seoData
    }
  };
}

export default Pricing;
